/**
 * Aviso antes de abandonar una pantalla con trabajo que no llegó al servidor.
 *
 * Las tres pantallas de carga larga —check list, orden correctiva y planilla de
 * mantenimiento— autoguardan contra el servidor. Cuando ese guardado falla, los
 * datos siguen en pantalla y el indicador se pone en rojo, pero **nada impide
 * cerrar la app o volver atrás**, y ahí sí se pierden: no hay cola local que los
 * retenga como en los partes diarios. En una obra con señal intermitente ese
 * final es el caso normal, no el raro.
 *
 * Dos caminos de salida, dos mecanismos, porque no hay uno solo que los cubra:
 *
 * - **Cerrar la app, recargar o cerrar la pestaña** → `beforeunload`. El texto
 *   lo elige el navegador (no se puede personalizar desde hace años); lo único
 *   que se decide acá es si el diálogo aparece.
 * - **Los botones "Volver" de la propia pantalla** → confirmación explícita,
 *   con el modal de siempre y un texto que dice qué se pierde.
 *
 * **Lo que NO cubre: el botón atrás del sistema.** Bloquear una navegación de
 * React Router necesita `useBlocker`, y `useBlocker` sólo funciona con un data
 * router (`createBrowserRouter`). La app monta `<BrowserRouter>` con `<Routes>`,
 * así que no está disponible. La alternativa —empujar una entrada centinela al
 * historial y escuchar `popstate`— rompe la navegación de formas sutiles y no
 * vale el riesgo en pantallas que ya están afinadas. Queda documentado como
 * hueco conocido en vez de tapado con algo frágil.
 */

import { useEffect, useRef } from 'react';
import { confirmarGlobal } from '../services/confirmBridge';

/** Texto único: los tres avisos dicen lo mismo, porque es el mismo riesgo. */
export const CONFIRMACION_SALIDA = {
  titulo: 'Hay cambios sin guardar',
  mensaje: 'Todavía hay cambios que no llegaron al servidor. Si salís ahora se '
    + 'pierden: esta pantalla no los guarda en el teléfono. Si estás sin señal, '
    + 'conviene esperar a recuperarla y que el aviso de guardado desaparezca.',
  labelConfirmar: 'Salir sin guardar',
  labelCancelar: 'Seguir acá',
  variante: 'danger',
};

/**
 * Resuelve el valor, sea un booleano o una función que lo calcula.
 *
 * Se acepta función porque en estas pantallas la señal de "falta guardar" vive
 * en refs (el temporizador del debounce, el buffer de borradores) que no
 * disparan render: un booleano capturado en el render quedaría viejo justo
 * cuando importa.
 */
const resolver = (valor) => (typeof valor === 'function' ? Boolean(valor()) : Boolean(valor));

/**
 * Hace que cerrar o recargar pida confirmación mientras haya cambios sin guardar.
 *
 * El listener se registra una sola vez y decide en el momento del evento, en vez
 * de re-registrarse con cada cambio: así lee el estado real y no el que había
 * cuando corrió el efecto.
 */
export function useAvisoAlCerrar(hayCambiosSinGuardar) {
  const ultimo = useRef(hayCambiosSinGuardar);
  ultimo.current = hayCambiosSinGuardar;

  useEffect(() => {
    const alCerrar = (e) => {
      if (!resolver(ultimo.current)) return;
      e.preventDefault();
      // Chrome todavía exige asignar `returnValue` para mostrar el diálogo.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', alCerrar);
    return () => window.removeEventListener('beforeunload', alCerrar);
  }, []);
}

/**
 * Pregunta antes de salir. `true` si se puede seguir con la salida.
 *
 * Sin cambios pendientes no molesta a nadie: devuelve `true` sin preguntar.
 */
export async function confirmarSalida(hayCambiosSinGuardar) {
  if (!resolver(hayCambiosSinGuardar)) return true;
  return confirmarGlobal(CONFIRMACION_SALIDA);
}

export default useAvisoAlCerrar;
